import { Button, DrawerBodyWrapper, H4, Input, TableActionButton, TOAST_TYPE } from '@base'
import { showLoader, showToast } from '@data/state/action'
import { EditDetailWrapper, Label } from '../styles'
import { MODAL_TYPES, strings } from '@constants'
import { downloadFile, updateOrganisationApi } from '@data'
import { toggleDrawer, toggleModal } from '@data/state/action/root'
import { fetchFinbudReport, getOrganisationCreditScoreAndPdf } from '@data/conn/get/finbud'
import styled from 'styled-components'
import { CloudDownloadOutlined } from '@material-ui/icons'
import { Spacings, Text } from '@styles'

const { useState, useEffect } = require('react')
const { useDispatch } = require('react-redux')

const InputItemWapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	height: 100%;
`
const ProprietorWapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
`

const CreditScoreWapper = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
`
const BorderWapper = styled.hr`
	border: 1px solid #f4f5f7;
`

const ActionButton = styled(TableActionButton)`
	margin: ${Spacings.SPACING_2};
`
const LabelWaaper = styled(H4)`
	color: ${({ theme }) => theme.palette.text.label};
	line-height: ${Text.LINE_HEIGHT_MAX};
	margin: ${({ margin }) => margin ?? 0};
	span {
		color: ${({ theme }) => theme.palette.text.black};
	}
`

const EditOrgPanDetails = ({ orgDetails, update }) => {
	const dispatch = useDispatch()
	const [orgPan, setOrgPan] = useState(orgDetails?.pan || '')
	const [legalName, setLegalName] = useState(orgDetails?.gst_details[0]?.legal_name || '')
	const [creditScore, setCreditScore] = useState(null)
	const [creditReportUrl, setCreditReportUrl] = useState(null)
	const isExport = orgDetails?.region === 'EXPORT'

	useEffect(() => {
		setOrgPan(orgDetails?.pan)
		setLegalName(orgDetails?.gst_details[0]?.legal_name)
		if (isValidPANFormat(orgDetails?.pan) && !isExport) {
			getOrgCreditScore()
		}
	}, [orgDetails])

	const getOrgCreditScore = () => {
		dispatch(showLoader(true))
		getOrganisationCreditScoreAndPdf(orgDetails?.pan, orgDetails.organisation_id)
			.then((res) => {
				setCreditScore(res.credit_score ? res.credit_score : null)
				setCreditReportUrl(res.credit_report_url ? res.credit_report_url : null)
			})
			.catch((error) => {
				dispatch(showToast(true, error?.response?.data?.message, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const isValidPANFormat = (pan) => {
		if (isExport) return Boolean(pan)
		return /^[A-Z]{3}P[A-Z][0-9]{4}[A-Z]$/.test(pan)
	}

	const onPanUpdate = () => (e) => {
		if (!orgPan) return

		e.stopPropagation()

		dispatch(
			toggleModal(true, MODAL_TYPES.CONFIRMATION, {
				overlayClickable: true,
				title: isExport ? strings(['confirm', 'to', 'update', 'registration']) : strings(['confirm', 'to', 'update', 'pan']),
				confirmAction: (actionType) => {
					dispatch(showLoader(true))
					if (actionType) {
						updateOrganisationApi(orgDetails.organisation_id, 'PAN', orgPan)
							.then(() => {
								dispatch(
									showToast(true, `Successfully updated organisation ${isExport ? 'registration' : 'pan'}`, {
										type: TOAST_TYPE.SUCCESS,
									})
								)
								dispatch(toggleDrawer(false))
								update()
							})
							.catch((err) => {
								console.log(err)
								dispatch(
									showToast(true, `Error while updating organisation ${isExport ? 'registration' : 'pan'}`, {
										type: TOAST_TYPE.ERROR,
									})
								)
							})
							.finally(() => {
								dispatch(showLoader(false))
							})
					} else {
						dispatch(showLoader(false))
					}
					dispatch(toggleModal(false))
				},
			})
		)
	}

	const mbookScoreHandler = () => {
		if (!legalName && !orgPan) return
		dispatch(showLoader(true))
		fetchFinbudReport(legalName, orgPan, orgDetails?.organisation_id)
			.then((response) => {
				dispatch(showToast(true, strings('msg_fetch_cibil_report_success'), { type: TOAST_TYPE.SUCCESS }))
				getOrgCreditScore()
				update()
			})
			.catch((error) => {
				dispatch(showToast(true, error.response.data.message || strings('msg_fetch_cibil_report_failure'), { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				dispatch(showLoader(false))
			})
	}

	const isDisabled = () => {
		if (isExport) return !orgPan
		let panRegex = /([A-Z]){5}([0-9]){4}([A-Z]){1}$/
		let gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
		if (panRegex.test(orgPan) || gstRegex.test(orgPan)) {
			return false
		}
		return true
	}

	const downloadBuyerPOHandler = () => {
		downloadFile(creditReportUrl, true)
	}

	return (
		<>
			<DrawerBodyWrapper>
				<InputItemWapper>
					<EditDetailWrapper>
						<Input
							{...(!isExport && { pattern: '[A-Z]{5}[0-9]{4}[A-Z]{1}' })}
							placeholder='Enter a name'
							label={isExport ? strings(['organisation', 'registration']) : strings(['organisation', 'pan'])}
							type='text'
							value={orgPan}
							maxLength='10'
							errorMap={{
								...(!isExport && { patternMismatch: strings('enter_valid_pan') }),
								valueMissing: strings('pan_required'),
							}}
							uppercase={!isExport}
							onChange={(val) => {
								setOrgPan(val)
							}}
						/>
						<div>
							<Button disabled={isDisabled()} onClick={onPanUpdate()}>
								Update
							</Button>
						</div>
					</EditDetailWrapper>
					{isValidPANFormat(orgPan) && !isExport && (
						<ProprietorWapper>
							<EditDetailWrapper>
								<Input
									placeholder='Enter a proprietor name'
									label='Proprietor Name'
									type='text'
									value={legalName}
									required
									onChange={(val) => {
										setLegalName(val)
									}}
								/>
								<div>
									<Button uppercase type='primary' disabled={!isValidPANFormat(orgPan) || !legalName} onClick={mbookScoreHandler}>
										{strings(['fetch', 'mbook', 'score'])}
									</Button>
								</div>
							</EditDetailWrapper>
							{creditScore && creditReportUrl && (
								<>
									<BorderWapper />

									<EditDetailWrapper>
										<CreditScoreWapper>
											<LabelWaaper>
												Mbook Score:{' '}
												<span>
													<strong>{creditScore ?? '-'} </strong>
												</span>
											</LabelWaaper>
											<ActionButton
												small
												uppercase
												disabled={!creditReportUrl}
												type='secondary'
												onClick={downloadBuyerPOHandler}
											>
												<CloudDownloadOutlined />
											</ActionButton>
										</CreditScoreWapper>
									</EditDetailWrapper>
								</>
							)}
						</ProprietorWapper>
					)}
				</InputItemWapper>
			</DrawerBodyWrapper>
		</>
	)
}

export default EditOrgPanDetails
